import { Component, Inject, OnInit, Optional } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  DialogRef,
  DialogService,
  DIALOG_DATA,
} from '@cyborg-monorepo/common/angular/cyborg-ui';
import {
  Enums,
  Environment,
  ExpandedHuntPackageRecord,
  ExpandedProfile,
  Hunt,
  Profile,
} from '@cyborg-monorepo/hunter/common';
import { RemoveTagEvent } from '@progress/kendo-angular-dropdowns';
import { forkJoin, map, Observable } from 'rxjs';
import { isHuntPackageHuntCompatible } from '../../../../helpers/utils';
import { CompanyService } from '../../../shared/services/company.service';
import { ContentService } from '../../../shared/services/content.service';
import { EnvironmentService } from '../../../shared/services/environment.service';
import { HuntService } from '../../../shared/services/hunt.service';

@Component({ standalone: false, 
  selector: 'edit-hunt-dialog',
  templateUrl: './edit-hunt-dialog.component.html',
  styleUrls: ['./edit-hunt-dialog.component.scss'],
})
export class EditHuntDialogComponent implements OnInit {
  public formGroup: FormGroup;
  public priorities: Array<string> = [];
  public assignees$: Observable<Array<Profile & ExpandedProfile>>;
  public environments: Environment[];
  constructor(
    public dialogRef: DialogRef,
    private formBuilder: FormBuilder,
    private companyService: CompanyService,
    public dialogService: DialogService,
    public huntService: HuntService,
    public contentService: ContentService,
    public environmentService: EnvironmentService,
    @Optional() @Inject(DIALOG_DATA) public hunt: Hunt,
  ) {}

  public ngOnInit(): void {
    this.environmentService.getAllEnvironments().subscribe((environments) => {
      this.environments = environments;
    });

    this.priorities = Object.values(Enums.PriorityEnum);
    this.assignees$ = this.companyService.getUsers().pipe(
      map((users: Array<Profile>) => {
        return users.map((user: Profile) => {
          return {
            ...user,
            ...{ fullName: user.firstName + ' ' + user.lastName },
          };
        });
      }),
    );

    this.formGroup = this.formBuilder.group({
      name: [this.hunt.name, Validators.required],
      environments: new FormControl(this.hunt.environments, [
        Validators.required,
      ]),
      assignee: [this.hunt.assignee],
      priority: [this.hunt.priority],
    });
  }

  public removeTag(value: RemoveTagEvent): void {
    this.hunt.environments = this.hunt.environments.filter(function (
      environment,
    ) {
      return environment.id != value.dataItem.id;
    });

    this.hunt.huntPackages = this.hunt.huntPackages.map((huntPack) => {
      huntPack.analysis = huntPack.analysis.filter(function (environment) {
        return environment.id != value.dataItem.id;
      });
      huntPack.originalAnalysis = huntPack.originalAnalysis.filter(function (
        environment,
      ) {
        return environment.id != value.dataItem.id;
      });
      return huntPack;
    });
  }

  public onValueChange(value: Environment[]): void {
    if (value.length > this.hunt.environments.length) {
      const generatedHuntPackages: Observable<ExpandedHuntPackageRecord>[] =
        this.hunt.huntPackages
          .filter((huntPackage) => {
            return isHuntPackageHuntCompatible(huntPackage);
          })
          .map((huntPackage) => {
            const content = this.contentService
              .getHuntPackageContent(huntPackage.UUID, {
                environments: [value[value.length - 1].id],
              })
              .pipe(
                map((generatedContent) => {
                  huntPackage.analysis.push(
                    generatedContent[0]
                      ? generatedContent[0]
                      : value[value.length - 1],
                  );
                  huntPackage.originalAnalysis.push(
                    generatedContent[0]
                      ? generatedContent[0]
                      : value[value.length - 1],
                  );
                  return huntPackage;
                }),
              );
            return content;
          });
      forkJoin(generatedHuntPackages).subscribe((generatedHuntPacks) => {
        this.hunt.huntPackages = generatedHuntPacks;
      });
    }
  }

  public onSubmit(): void {
    this.huntService
      .updateHunt(this.hunt.id, {
        ...this.formGroup.value,
        huntPackages: this.hunt.huntPackages,
      })
      .subscribe((hunt: Hunt) => {
        this.dialogRef.close(hunt);
      });
  }

  public onClose(): void {
    this.dialogRef.close();
  }
}
